import React, {FC, memo, useState} from "react";
import {Alert, Button, message, Radio, Select, Space, Table, Tag, Tooltip} from "antd";
import {CheckCircleOutlined, EditOutlined, EuroCircleOutlined, FileTextOutlined, LockOutlined, UndoOutlined} from "@ant-design/icons";
import {ColumnProps, TablePaginationConfig} from "antd/lib/table";
import {ApolloError} from "@apollo/client";
import moment, {Moment} from "moment";
import classNames from "classnames";
import {green, red, yellow} from "@ant-design/colors";
import {
    ArrayDataOptions,
    BookingType,
    GetBookingsByCourseIdWithCourseLessonBookingsQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    LessonBookingIntentInput,
    LessonBookingPlannerPreviewQuery,
    LessonBookingPlannerRateStatus,
    LessonBookingPlannerUnitStatus,
    LessonBookingRateEditInput,
    LessonPricingMode,
    Maybe,
    PaymentType,
} from "../../../generated/graphql";
import SummarySection from "../../../views/SummarySection/SummarySection";
import LoadingContent from "../../../views/LoadingContent/LoadingContent";
import {getPagination} from "../../../helpers/getPagination";
import {renderDateAsWeekdayTag, renderDateFormatted} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {colorGrey} from "../../../styles/colors";
import i18n from "../../../services/i18n";
import Notes from "./Notes";
import {DummySwitchMonthlyPayment} from "./PaymentLists/SwitchMonthlyPayment";
import paymentCss from "./PaymentLists/PaymentLists.less";
import {renderIndex} from "./helper";
import {defaultPageSize} from "./ModalContainer";
import DateRangePicker from "./DateRangePicker";
import type {LessonOfTheCourseType} from "./PreviewBooking";
import css from "./LessonBookings.less";
import PlannerSummary from "./PlannerSummary";
import {changed, date, day, eur, signed} from "./plannerFormat";
import RateEdit, {NEW_RATE_ID} from "./RateEdit";

// Vorschau „Neu“ (Superuser), wie die bisherige Ansicht: links „Bisherige Buchung“, rechts „Vorschau der geänderten Buchung“.
// Beide Spalten entstehen aus EINER Zeilenliste (Monate, Kurseinheiten, Raten) mit fester Zeilenhöhe, damit Einheit n links
// und rechts auf gleicher Höhe steht; was es nur auf einer Seite gibt, bekommt gegenüber eine leere Zeile. Raten und
// „Gesamtpreis“ sehen aus wie in der bisherigen Ansicht (PaymentLists). Geändertes wird rechts hervorgehoben; darüber die
// Kurzfassung (PlannerSummary), darunter, was das Büro entscheidet. Mit `onIntentChange` (Stufe 4) entscheidet das Büro je Buchung:
// Haken rechts oder Zeitraum, Preis nach Formel oder individuell (unter dem Gesamtpreis), Monatsversatz bei mehrdeutiger
// Zuordnung, Notiz Mitarbeiterin der Buchung.
// Der Server plant mit diesen Absichten neu; die Vorschau zeigt immer seinen Plan.

type PlannerData = LessonBookingPlannerPreviewQuery["lessonBookingPlannerPreview"];
type PlannerRate = PlannerData["rates"][number];

type MonthRow = {key: string; label: string; before: number; after: number; removed: Array<string>; added: Array<string>; moved: Array<string>; more?: boolean};

type LessonRow = {
    key: string;
    index: number;
    startDateTime: Date | string;
    before: boolean;
    after: boolean;
    status?: LessonBookingPlannerUnitStatus;
    movedFrom?: string;
    unresolved?: boolean;
    existed: boolean; // Einheit gab es schon vor der Änderung (sonst links leere Zeile)
    inCourse: boolean; // Einheit steht nach der Änderung im Kurs, kann also angehakt werden
    removedFromCourse?: boolean; // Einheit stand vor der Änderung im Kurs und wurde daraus entfernt
};
type FormLesson = {id?: string | null; startDateTime: Date | string; indexOfLessonInCourse?: number | null};

type PlannerPreviewProps = {
    data?: PlannerData;
    loading: boolean;
    error?: ApolloError;
    booking?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
    lessonsOfCourse?: Array<FormLesson>; // Kurseinheiten aus dem Formular (nach der Änderung); ohne id = neu, Platzhalter wie im Planner
    lessonsBefore?: Array<FormLesson>; // Kurseinheiten vor der Änderung (gespeicherter Stand): aus dem Kurs entfernte bleiben sichtbar
    getHolidayInfo?: (date: Moment) => GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined;
    options?: Maybe<ArrayDataOptions>; // Blättern, geteilt mit der bisherigen Ansicht
    setOptions?: (options: ArrayDataOptions) => void;
    allowMonthlyPayment?: boolean; // für die Zahlweise-Anzeige wie in der bisherigen Ansicht
    existingLessonIds?: Set<string>; // IDs der gespeicherten Kurseinheiten; „Einheit hinzufügen“ vergibt schon eine vorläufige ID
    intent?: LessonBookingIntentInput;
    onIntentChange?: (patch: Partial<LessonBookingIntentInput> | null) => void;
    holidays?: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"];
    // Teilnehmer, Bucher und Abrechnungs-Hinweis; steht unter der Kurzfassung (Variante A, 27.10.2026).
    header?: React.ReactNode;
    // Kurseinheiten aufgeklappt; gesteuert, damit der Zustand beim Blättern durch die Buchungen bleibt.
    lessonsExpanded?: boolean;
    onLessonsExpandedChange?: (expanded: boolean) => void;
};

// Monat der Rate = Monat der Einheit + Versatz (ADR 0020 Punkt 3).
const OFFSET_OPTIONS = [
    {value: -1, label: "einen Monat vorher"},
    {value: 0, label: "im selben Monat"},
    {value: 1, label: "einen Monat später"},
];
const EMPTY_SELECTION = "Eine Buchung braucht mindestens eine Kurseinheit. Zum Beenden die Buchung stornieren.";

const monthChanged = (row: MonthRow) => row.removed.length + row.added.length + row.moved.length > 0;

// Lange Listen (Jahreskurs: 13 Monate, 13 Raten) zeigen nur geänderte Zeilen und ihre Nachbarn; der Rest wird je Lücke
// eine Zeile „… n weitere“, ein Klick zeigt alle (Variante A, 27.10.2026). Ohne Änderung bleiben die ersten drei sichtbar.
const SHORTEN_FROM = 7;
export type Segment = {start: number; end: number; hidden: boolean};
export const segments = (changedFlags: Array<boolean>, showAll: boolean): Array<Segment> => {
    const n = changedFlags.length;
    const anyChanged = changedFlags.some(Boolean);
    const keep = changedFlags.map((_, i) => showAll || n < SHORTEN_FROM || (anyChanged ? changedFlags.slice(Math.max(0, i - 1), i + 2).some(Boolean) : i < 3));
    const result: Array<Segment> = [];

    keep.forEach((kept, i) => {
        const last = result[result.length - 1];

        if (last && last.hidden === !kept) last.end = i;
        else result.push({start: i, end: i, hidden: !kept});
    });
    // Eine einzelne versteckte Zeile spart keinen Platz und kostet einen Klick (Review #1728): sichtbar lassen.
    result.forEach((segment) => {
        if (segment.hidden && segment.end === segment.start) segment.hidden = false;
    });

    return result.reduce((merged, segment) => {
        const last = merged[merged.length - 1];

        if (last && last.hidden === segment.hidden) last.end = segment.end;
        else merged.push({...segment});

        return merged;
    }, [] as Array<Segment>);
};
const changedCell = {backgroundColor: yellow[1], fontWeight: 600};
const blank = <span style={{color: "#bbb"}}>{"–"}</span>;

const deltaText = (delta: number, unit: "units" | "eur") => {
    if (unit === "units" ? delta === 0 : !changed(delta, 0)) return null;
    const text = unit === "units" ? `${delta > 0 ? "+" : "−"}${Math.abs(delta)}` : signed(delta);

    return <span style={{color: delta < 0 ? red[6] : green[7], fontWeight: 600}}>{text}</span>;
};

// Heute zählt am alten Datum, Neu am neuen (verschobene Einheit: gleiche ID, anderer Tag → beide Monate mit Hinweis).
const groupByMonth = (units: PlannerData["units"]): Array<MonthRow> => {
    const rows = new Map<string, MonthRow>();
    const rowFor = (value: string | Date) => {
        const m = moment(value);
        const key = m.format("YYYY-MM");
        const row = rows.get(key) ?? {key, label: m.format("MMMM YYYY"), before: 0, after: 0, removed: [], added: [], moved: []};

        rows.set(key, row);

        return row;
    };

    units.forEach((unit) => {
        const before = rowFor(unit.lessonDate);
        const after = rowFor(unit.movedTo ?? unit.lessonDate);

        if (unit.status !== LessonBookingPlannerUnitStatus.Added) before.before += 1;
        if (unit.status !== LessonBookingPlannerUnitStatus.Removed) after.after += 1;
        if (unit.status === LessonBookingPlannerUnitStatus.Removed) before.removed.push(day(unit.lessonDate));
        if (unit.status === LessonBookingPlannerUnitStatus.Added) after.added.push(day(unit.lessonDate));
        if (unit.movedTo) {
            const text = `${day(unit.lessonDate)} → ${day(unit.movedTo)}`;

            before.moved.push(text);
            if (after !== before) after.moved.push(text);
        }
    });

    return Array.from(rows.values()).sort((a, b) => a.key.localeCompare(b.key));
};

// Alle Kurseinheiten mit Haken „Heute“ und „Neu“; aus dem Kurs gelöschte Einheiten kommen aus den Planner-Units dazu, nicht
// gebuchte gelöschte aus `lessonsBefore` (beide Seiten zeigen sie, rechts mit „aus dem Kurs entfernt“).
// Ohne `existingLessonIds` zählt eine Einheit mit ID als vorhanden (nur „Neu generieren“ erzeugt Einheiten ohne ID).
export const lessonRows = (units: PlannerData["units"], lessonsOfCourse: Array<FormLesson>, existingLessonIds?: Set<string>, lessonsBefore?: Array<FormLesson>): Array<LessonRow> => {
    const unitsById = new Map(units.map((unit) => [unit.lessonId, unit]));
    const rows: Array<LessonRow> = lessonsOfCourse.map((lesson, index) => {
        const id = lesson.id ?? `new-${index}`;
        const unit = unitsById.get(id);

        unitsById.delete(id);

        return {
            key: id,
            index,
            startDateTime: lesson.startDateTime,
            before: !!unit && unit.status !== LessonBookingPlannerUnitStatus.Added,
            after: !!unit && unit.status !== LessonBookingPlannerUnitStatus.Removed,
            status: unit?.status,
            // Die Zeile trägt das neue Datum aus dem Formular; links steht das alte, auch wenn die Einheit zugleich wegfällt
            // (der Planner setzt movedTo nur für behaltene Einheiten).
            movedFrom: unit && !moment(unit.lessonDate).isSame(lesson.startDateTime, "day") ? unit.lessonDate : undefined,
            existed: Boolean(lesson.id) && (existingLessonIds ? existingLessonIds.has(lesson.id as string) : true),
            inCourse: true,
        };
    });

    // Einheiten ohne Treffer im Formular: aus dem Kurs gelöscht (Removed) oder nach „Neu generieren“ mit alter ID als Kept (Zuordnung offen).
    unitsById.forEach((unit) =>
        rows.push({
            key: unit.lessonId,
            index: -1,
            startDateTime: unit.lessonDate,
            before: unit.status !== LessonBookingPlannerUnitStatus.Added,
            after: unit.status !== LessonBookingPlannerUnitStatus.Removed,
            status: unit.status,
            unresolved: unit.status === LessonBookingPlannerUnitStatus.Kept,
            existed: true,
            inCourse: false,
            removedFromCourse: unit.status === LessonBookingPlannerUnitStatus.Removed,
        }),
    );
    const known = new Set(rows.map((row) => row.key));

    (lessonsBefore ?? []).forEach((lesson) => {
        if (!lesson.id || known.has(lesson.id)) return;
        rows.push({key: lesson.id, index: -1, startDateTime: lesson.startDateTime, before: false, after: false, existed: true, inCourse: false, removedFromCourse: true});
    });
    rows.sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());
    // Nummer = Position in der sortierten Gesamtliste, links wie rechts dieselbe. Die Kursnummerierung rückt nach einer
    // Löschung nach (13.03. würde zur 5), der Vergleich nicht: gelöschte 06.03. bleibt die 5, 13.03. die 6.
    rows.forEach((row, position) => {
        row.index = position;
    });

    return rows;
};

const PlannerPreview: FC<PlannerPreviewProps> = ({
    data,
    loading,
    error,
    booking,
    lessonsOfCourse,
    lessonsBefore,
    getHolidayInfo,
    options,
    setOptions,
    allowMonthlyPayment,
    existingLessonIds,
    intent,
    onIntentChange,
    holidays,
    header,
    lessonsExpanded: controlledLessonsExpanded,
    onLessonsExpandedChange,
}) => {
    const [showPicker, setShowPicker] = useState(false);
    const [showAllMonths, setShowAllMonths] = useState(false);
    const [showAllRates, setShowAllRates] = useState(false);
    const [localLessonsExpanded, setLocalLessonsExpanded] = useState(false);
    const lessonsExpanded = controlledLessonsExpanded ?? localLessonsExpanded;
    const setLessonsExpanded = onLessonsExpandedChange ?? setLocalLessonsExpanded;
    const panels = (left: React.ReactNode, right: React.ReactNode, titled = true) => (
        // height: auto statt 100 % aus dem Stylesheet: sonst laufen die Spalten über und die Hinweise darunter überlagern sie.
        <div className={css.bookingContainer} style={{height: "auto"}}>
            <div className={css.modalContainerLeft}>
                {titled && <div className={css.header}>{"Bisherige Buchung"}</div>}
                {left}
            </div>
            <div className={css.modalContainerRight}>
                {titled && <div className={css.header}>{"Vorschau der geänderten Buchung"}</div>}
                {right}
            </div>
        </div>
    );

    if (error) {
        return (
            <>
                {header}
                {panels(
            null,
            <div style={{padding: "1rem"}}>
                <Alert type="error" showIcon message="Vorschau (neues Modell) konnte nicht berechnet werden" description={error.message} />
                {intent && onIntentChange && (
                    // Lehnt der Server eine Entscheidung ab, muss sie sich hier verwerfen lassen, sonst scheitert auch das Speichern.
                    <Button type="link" size="small" onClick={() => onIntentChange(null)}>
                        {"Zurück zum ursprünglichen Vorschlag"}
                    </Button>
                )}
            </div>,
                )}
            </>
        );
    }
    if (loading || !data) {
        return (
            <>
                {header}
                {panels(
                    null,
                    <div style={{height: "70%"}}>
                        <LoadingContent content="Vorschau (neues Modell) wird berechnet..." />
                    </div>,
                )}
            </>
        );
    }

    const allMonths = groupByMonth(data.units);
    const months: Array<MonthRow> = segments(allMonths.map(monthChanged), showAllMonths).map((segment) => {
        if (!segment.hidden) return allMonths.slice(segment.start, segment.end + 1);
        const hidden = allMonths.slice(segment.start, segment.end + 1);
        const sum = (pick: (row: MonthRow) => number) => hidden.reduce((total, row) => total + pick(row), 0);

        return [{key: `more-${segment.start}`, label: `… ${hidden.length} weitere ${hidden.length === 1 ? "Monat" : "Monate"}`, before: sum((row) => row.before), after: sum((row) => row.after), removed: [], added: [], moved: [], more: true}];
    }).reduce((all, rows) => all.concat(rows), [] as Array<MonthRow>);
    const priceChanged = changed(data.priceAfter, data.priceBefore);
    const hasUnitPrice = data.unitPrice !== null && data.unitPrice !== undefined;

    const unitPrice = data.unitPrice ?? 0;
    // Individueller Preis: Einheiten ändern den Preis nicht; mit Verrechnung (Zehnerkarte) doch.
    const individualMode = data.pricingMode === LessonPricingMode.Individual;
    const chargeUnitChanges = individualMode && Boolean(intent?.chargeUnitChanges);
    const removedUnits = data.units.filter((unit) => unit.status === LessonBookingPlannerUnitStatus.Removed).length;
    const addedUnits = data.units.filter((unit) => unit.status === LessonBookingPlannerUnitStatus.Added).length;
    const handEdited = data.rates.some((rate) => rate.edited);

    // Gesamtpreis = Einheiten × Einheitenpreis + Altbestand + Gebühren/Zusatzposten/Gutschriften (Planner-Invariante, beide Seiten).
    // Die Gleichung steht nur, wenn sie aufgeht; sonst erklären die Hinweise den Preis.
    const term = (amount: number, label: string) => (changed(amount, 0) ? ` ${amount < 0 ? "−" : "+"} ${eur(Math.abs(amount))} ${label}` : "");
    const priceFormula = (after: boolean) => {
        if (!hasUnitPrice) return "Pauschalpreis pro Kurs";
        if (after && data.canceled) return "unverändert";
        // Zehnerkarte mit Verrechnung: der Paketpreis ist die Basis, die Formel geht nicht auf und soll es auch nicht.
        if (chargeUnitChanges) return after ? `Paketpreis, Einheitenänderungen zu ${eur(unitPrice)} verrechnet` : "Paketpreis";
        const units = after ? data.bookedUnitsAfter : data.bookedUnitsBefore;
        const price = after ? data.priceAfter : data.priceBefore;

        // Bei storniert oder „neu erzeugt“ rechnet der Planner keinen Altbestand aus: dann lieber nichts als ein Hinweis, den es nicht gibt.
        const formula = units * (data.unitPrice as number) + data.legacyGap + data.nonCoursePostsSum;
        // Raten von Hand in diesem Dialog: der Preis folgt den Raten, ihr Anteil ist ein eigener Posten (Stage-Abnahme 27.10.).
        const byHand = after ? data.handDelta ?? 0 : 0;

        if (changed(formula + byHand, price)) return data.canceled || data.regenerated ? "" : "siehe Hinweise";

        return `${units} × ${eur(data.unitPrice as number)}${term(data.legacyGap, "„Altbestand“")}${term(data.nonCoursePostsSum, "Gebühren/Zusatzposten/Gutschriften")}${term(byHand, "von Hand")}`;
    };

    const lessons = lessonsOfCourse ? lessonRows(data.units, lessonsOfCourse, existingLessonIds, lessonsBefore) : [];
    // Bearbeiten nur, wo der Server die Auswahl übernimmt: nicht bei storniert und nicht nach „Lektionen neu generieren“.
    const editable = Boolean(onIntentChange) && !data.canceled && !data.regenerated;
    // Die eigene Auswahl gilt sofort, nicht erst mit dem nächsten Plan: sonst überschreibt ein zweiter Klick während der
    // Entprellung den ersten (/code-review #1674).
    const selectedKeys = intent?.courseLessonIds ?? lessons.filter((row) => row.after && row.inCourse).map((row) => row.key);
    const selectLessons = (keys: Array<string>) => {
        if (keys.length === 0) {
            message.warning(EMPTY_SELECTION);

            return;
        }
        onIntentChange?.({courseLessonIds: keys});
    };
    const courseLessonsForPicker = lessons.filter((row) => row.inCourse).map((row) => ({id: row.key, startDateTime: row.startDateTime})) as unknown as LessonOfTheCourseType;
    // Beide Terminlisten blättern über dieselben Optionen, sonst zeigen sie verschiedene Seiten.
    const pagination: TablePaginationConfig = {
        current: options?.offset && options?.limit ? options.offset / options.limit + 1 : 1,
        pageSize: options?.limit ?? defaultPageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "15", "20", "25", "50", "100"],
        total: lessons.length,
        size: "small",
        showTotal: (total, range) => `${range[0]}-${range[1]} von ${total}`,
    };
    const onTableChange = (next: TablePaginationConfig) => {
        const {limit, offset} = getPagination({pagination: next, defaultSize: defaultPageSize});

        setOptions?.({...options, limit, offset});
    };

    // Spalten je Seite: links der Stand heute ohne Hervorhebung, rechts der neue Stand mit Delta, Grund und gelben Zellen.
    const monthColumns = (after: boolean): Array<ColumnProps<MonthRow>> => [
        {title: "Monat", dataIndex: "label", key: "label"},
        ...(after ? [{title: "", key: "delta", align: "center" as const, width: 50, render: (_: unknown, row: MonthRow) => deltaText(row.after - row.before, "units")}] : []),
        {
            title: "Einheiten",
            key: "units",
            align: "right",
            width: 90,
            onCell: (row) => ({style: after && monthChanged(row) ? changedCell : {}}),
            render: (_, row) => (after ? row.after : row.before),
        },
        ...(after
            ? [
                  {
                      title: "Grund",
                      key: "reason",
                      ellipsis: true,
                      onCell: (row: MonthRow) => ({style: monthChanged(row) ? changedCell : {}}),
                      render: (_: unknown, row: MonthRow) => (
                          <>
                              {row.removed.length > 0 && <span style={{color: red[6]}}>{`Ausfall ${row.removed.join(", ")}`}</span>}
                              {row.added.length > 0 && <span style={{color: green[7]}}>{`${row.removed.length > 0 ? " · " : ""}Zubuchung ${row.added.join(", ")}`}</span>}
                              {row.moved.length > 0 && <span style={{color: "#666"}}>{`${row.removed.length + row.added.length > 0 ? " · " : ""}Verschoben ${row.moved.join(", ")}`}</span>}
                          </>
                      ),
                  },
              ]
            : []),
    ];

    const lessonColumns = (after: boolean): Array<ColumnProps<LessonRow>> => {
        const shown = (row: LessonRow) => after || row.existed;
        const dateOf = (row: LessonRow) => (after ? row.startDateTime : row.movedFrom ?? row.startDateTime);

        return [
            {title: "#", key: "index", align: "center", width: 50, render: (_, row) => (shown(row) ? renderIndex(row.index, getHolidayInfo?.(moment(dateOf(row)))) : null)},
            {
                title: i18n.containers.courses.CourseCreate.courseStartDateTime.weekday(),
                key: "weekday",
                width: 110,
                render: (_, row) => (shown(row) ? renderDateAsWeekdayTag({value: moment(dateOf(row)).toISOString(), style: {width: "100%", textAlign: "center", fontWeight: 600}}) : null),
            },
            {
                title: i18n.containers.courses.CourseCreate.courseStartDateTime.date(),
                key: "date",
                onCell: (row) => ({style: after && row.movedFrom ? changedCell : {}}),
                render: (_, row) => (shown(row) ? renderDateFormatted(moment(dateOf(row)).toISOString(), "DD. MMM YYYY") : blank),
            },
            ...(after
                ? [
                      {
                          title: "Grund",
                          key: "reason",
                          ellipsis: true,
                          onCell: (row: LessonRow) => ({style: row.before !== row.after || row.movedFrom || row.unresolved ? changedCell : {}}),
                          render: (_: unknown, row: LessonRow) =>
                              row.unresolved ? (
                                  <span style={{color: "#666"}}>{"Zuordnung offen"}</span>
                              ) : row.status === LessonBookingPlannerUnitStatus.Removed ? (
                                  <span style={{color: red[6]}}>{row.removedFromCourse ? "Ausfall, aus dem Kurs entfernt" : "Ausfall"}</span>
                              ) : row.removedFromCourse ? (
                                  <span style={{color: "#999"}}>{"aus dem Kurs entfernt"}</span>
                              ) : row.status === LessonBookingPlannerUnitStatus.Added ? (
                                  <span style={{color: green[7]}}>{"Zubuchung"}</span>
                              ) : row.movedFrom ? (
                                  <span style={{color: "#666"}}>{`Verschoben von ${day(row.movedFrom)}`}</span>
                              ) : null,
                      },
                  ]
                : []),
        ];
    };

    const lessonRowClass = (row: LessonRow, after: boolean) => {
        if (after && row.status === LessonBookingPlannerUnitStatus.Removed) return classNames(css.alignedRow, css.canceledBooking, css.unbookedLesson);
        if (after && row.status === LessonBookingPlannerUnitStatus.Added) return classNames(css.alignedRow, css.newBooking, css.bookedLesson);
        if (!after && !row.existed) return css.alignedRow;

        return classNames(css.alignedRow, (after ? row.after : row.before) ? css.bookedLesson : css.unbookedLesson);
    };

    // Offene und neue Raten lassen sich von Hand ändern (Betrag, Fälligkeit, Löschen); der Server rechnet mit.
    const rateEditable = editable && data.rateEditsAllowed;
    const rateEdits = intent?.rateEdits ?? [];
    const changeRateEdit = (rateId: string, edit: LessonBookingRateEditInput | null) =>
        onIntentChange?.({rateEdits: rateEdits.filter((other) => other.rateId !== rateId).concat(edit ? [edit] : [])});

    // Raten wie in der bisherigen Ansicht (PaymentsPassive): Nummer, Monat, „Fällig am“ bzw. Bindungsgrund, Betrag.
    // Feste Mindesthöhe, damit die leere Gegenzeile einer neuen Rate links gleich hoch ist.
    const entryStyle = {minHeight: 26};
    const amountStyle = {whiteSpace: "nowrap" as const}; // Delta + Betrag bleiben eine Zeile, sonst wird die Zeile rechts höher
    const rateEntry = (rate: PlannerRate, position: number, after: boolean) => {
        const key = rate.id ?? `new-${String(rate.paymentDue)}`;
        const isNew = rate.status === LessonBookingPlannerRateStatus.New;

        if (!after && isNew) {
            return (
                <div key={key} className={paymentCss.monthlyPaymentEntry}>
                    <div className={paymentCss.mainDue} style={entryStyle}>
                        <div className={paymentCss.month}>
                            <span className={paymentCss.num}>{"–"}</span>
                        </div>
                    </div>
                </div>
            );
        }
        const bound = rate.status === LessonBookingPlannerRateStatus.Bound;
        const paid = rate.boundReason === "bezahlt"; // „teilbezahlt“ bleibt offen dargestellt (Schloss, kein Grün)
        const delta = after ? rate.amountAfter - rate.amountBefore : 0;
        const isChanged = after && !isNew && (changed(delta, 0) || Boolean(rate.edited)); // neue Rate: „neu“ statt Delta
        const adjustments = rate.adjustments.map((adjustment) => `${adjustment.reason} ${signed(adjustment.amount)}`).join(", ");
        // Links der Stand heute: eine von Hand verschobene Rate steht dort mit ihrer alten Fälligkeit.
        const due = after ? rate.paymentDue : rate.paymentDueBefore ?? rate.paymentDue;
        const rateId = rate.id ?? NEW_RATE_ID;

        return (
            <div key={key} className={classNames(paymentCss.monthlyPaymentEntry, isChanged && paymentCss.changedPayment)} style={after && isNew ? {backgroundColor: green[1]} : undefined}>
                <div className={paid ? paymentCss.mainDone : paymentCss.mainDue} style={entryStyle}>
                    <div className={paymentCss.month}>
                        <span className={paymentCss.num}>{`${position + 1}.`}</span>
                        <span>{moment(due).format("MMM YY")}</span>
                        {after && isNew && (
                            <Tag color="green" style={{marginLeft: 8}}>
                                {"neu"}
                            </Tag>
                        )}
                        {/* Icons statt Tags: die Zeile bleibt so hoch wie links (Browser-Test 27.10.) */}
                        {after && rate.edited && !rate.deleted && (
                            <Tooltip title="von Hand geändert">
                                <EditOutlined style={{marginLeft: 8, color: "#fa8c16"}} aria-label="von Hand geändert" />
                            </Tooltip>
                        )}
                        {after && rate.note && (
                            <Tooltip title={rate.note}>
                                <FileTextOutlined style={{marginLeft: 8, color: colorGrey}} aria-label="Notiz zum Posten" />
                            </Tooltip>
                        )}
                    </div>
                    <div className={paymentCss.paymentDueContainer}>
                        <div className={paymentCss.paymentDue} style={after && rate.paymentDueBefore ? {...changedCell, whiteSpace: "nowrap"} : undefined}>
                            {bound ? rate.boundReason : after && rate.deleted ? <span style={{color: red[6]}}>{"gelöscht"}</span> : `Fällig am ${date(due)}`}
                        </div>
                        {paid ? (
                            <CheckCircleOutlined style={{color: green[5], fontSize: 13}} />
                        ) : bound ? (
                            <LockOutlined style={{color: colorGrey, fontSize: 13}} />
                        ) : (
                            <EuroCircleOutlined style={{color: colorGrey, fontSize: 13}} />
                        )}
                    </div>
                    <div className={paymentCss.amount} style={amountStyle}>
                        {isChanged && (
                            <Tooltip title={adjustments || undefined}>
                                <span style={{marginRight: 8}}>{deltaText(delta, "eur")}</span>
                            </Tooltip>
                        )}
                        <span style={after && rate.deleted ? {textDecoration: "line-through", color: "#999"} : undefined}>{eur(after && !rate.deleted ? rate.amountAfter : rate.amountBefore)}</span>
                        {after && rateEditable && !bound && (
                            <span style={{marginLeft: 4}}>
                                <RateEdit
                                    rateId={rateId}
                                    amount={rate.amountAfter}
                                    paymentDue={rate.paymentDue}
                                    note={rate.note}
                                    edit={rateEdits.find((edit) => edit.rateId === rateId)}
                                    deleted={Boolean(rate.deleted)}
                                    onChange={(edit) => changeRateEdit(rateId, edit)}
                                />
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const rateSegments = segments(
        // Auch Anpassungen, die sich aufheben, bleiben sichtbar: ihr Tooltip nennt die Gründe (Review #1728).
        data.rates.map((rate) => rate.status === LessonBookingPlannerRateStatus.New || changed(rate.amountAfter, rate.amountBefore) || rate.adjustments.length > 0 || Boolean(rate.edited)),
        showAllRates,
    );

    const baselineIndividual = data.pricingModeBefore === LessonPricingMode.Individual;
    const unitsCharged = !individualMode || chargeUnitChanges;
    const choiceMade = (intent?.individualPrice !== null && intent?.individualPrice !== undefined) || (intent?.chargeUnitChanges !== null && intent?.chargeUnitChanges !== undefined);
    const showUnitChoice = editable && hasUnitPrice && (removedUnits + addedUnits > 0 || choiceMade);
    // Individueller Preis (Zehnerkarte, Freiplatz, Zahlweise individuell): das Büro kann bewusst auf die Formel umstellen
    // (Entscheidung 27.10.2026, Codex #1743). Eigener Link, damit die Frage zu den Einheiten zwei Antworten behält.
    const switchedToFormula = intent?.individualPrice === false;
    const showFormulaSwitch = editable && hasUnitPrice && baselineIndividual;
    const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
    const unitChoiceLabel = `${[removedUnits > 0 ? count(removedUnits, "Einheit entfällt", "Einheiten entfallen") : "", addedUnits > 0 ? count(addedUnits, "Einheit kommt dazu", "Einheiten kommen dazu") : ""]
        .filter(Boolean)
        .join(", ") || "Geänderte Einheiten"}:`;

    // „Gesamtpreis“ wie in der bisherigen Ansicht; die gelbe Markierung tragen beide Seiten, damit sie gleich hoch bleiben.
    const totalPrice = (after: boolean) => (
        <>
            <div className={classNames(paymentCss.totalPrice, priceChanged && paymentCss.totalPriceChanged)}>
                <div>{"Gesamtpreis"}</div>
                <div style={amountStyle}>
                    {after && priceChanged && <span style={{fontSize: 14, marginRight: 8}}>{deltaText(data.priceAfter - data.priceBefore, "eur")}</span>}
                    {eur(after ? data.priceAfter : data.priceBefore)}
                </div>
            </div>
            {/* Eine Zeile auf beiden Seiten, damit die Spalten bündig bleiben; der volle Text steht im Tooltip. */}
            <div className={paymentCss.totalPriceSub} style={{minHeight: 18, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}} title={priceFormula(after)}>
                {priceFormula(after)}
            </div>
            {/* Eine Frage zur Einheitenänderung statt Preismodus und Zehnerkarten-Wahl (Stage-Abnahme 27.10.): „verrechnen“
                kehrt zum Modus von vorher zurück (Formel, bei individuellem Preis mit Verrechnung), „Preis bleibt“ stellt auf
                individuell. Sichtbar bei Ausfall oder Zubuchung und solange eine Wahl getroffen ist; links eine gleich hohe Leerzeile. */}
            {(showUnitChoice || showFormulaSwitch) && (
                <div style={{minHeight: (showUnitChoice ? 52 : 0) + (showFormulaSwitch ? 24 : 0), marginTop: 4}}>
                    {after && showFormulaSwitch && (
                        <Button
                            type="link"
                            size="small"
                            style={{padding: 0, height: 24}}
                            onClick={() => onIntentChange?.(switchedToFormula ? {individualPrice: null, chargeUnitChanges: null} : {individualPrice: false, chargeUnitChanges: null})}
                        >
                            {switchedToFormula ? "zurück zum individuellen Preis" : `auf Einheiten × ${eur(unitPrice)} umstellen`}
                        </Button>
                    )}
                    {after && showUnitChoice && (
                        <>
                            <div>{unitChoiceLabel}</div>
                            <Radio.Group
                                size="small"
                                aria-label="Geänderte Einheiten"
                                value={unitsCharged ? "charge" : "keep"}
                                onChange={(event) =>
                                    onIntentChange?.(
                                        event.target.value === "keep"
                                            ? {individualPrice: true, chargeUnitChanges: false}
                                            : baselineIndividual
                                            ? {individualPrice: true, chargeUnitChanges: true}
                                            : {individualPrice: null, chargeUnitChanges: null},
                                    )
                                }
                            >
                                <Tooltip title="Ein Ausfall mindert die nächste offene Rate (sonst Guthaben), eine Zubuchung wird berechnet.">
                                    <Radio value="charge" style={{whiteSpace: "nowrap"}}>{`verrechnen (${eur(unitPrice)} je Einheit)`}</Radio>
                                </Tooltip>
                                <Radio value="keep" style={{whiteSpace: "nowrap"}}>{"Preis bleibt"}</Radio>
                            </Radio.Group>
                        </>
                    )}
                </div>
            )}
        </>
    );

    const side = (after: boolean) => (
        <div className={css.bookingRoot}>
            <SummarySection
                title="Kurseinheiten je Monat"
                content={
                    <Table<MonthRow>
                        size="small"
                        pagination={false}
                        rowKey="key"
                        dataSource={months}
                        columns={monthColumns(after)}
                        rowClassName={(row) => classNames(css.alignedRow, row.more && css.moreRow)}
                        onRow={(row) => (row.more ? {onClick: () => setShowAllMonths(true), title: "Alle Monate zeigen"} : {})}
                    />
                }
            />
            <SummarySection
                title={i18n.containers.bookings.BookingSummary.sectionHeaders.installmentRates()}
                style={{marginTop: 12}}
                content={
                    <div>
                        {booking && <DummySwitchMonthlyPayment paymentType={booking.paymentType as PaymentType} allowUpFrontPayment={true} allowMonthlyPayment={allowMonthlyPayment} />}
                        {rateSegments.map((segment) =>
                            segment.hidden ? (
                                <div
                                    key={`more-${segment.start}`}
                                    className={classNames(paymentCss.monthlyPaymentEntry, css.moreRow)}
                                    onClick={() => setShowAllRates(true)}
                                    title="Alle Raten zeigen"
                                >
                                    <div className={paymentCss.mainDue} style={entryStyle}>
                                        <div className={paymentCss.month}>{`… ${segment.end - segment.start + 1} weitere ${segment.end === segment.start ? "Rate" : "Raten"}`}</div>
                                        <div className={paymentCss.amount} style={amountStyle}>
                                            {eur(
                                                data.rates
                                                    .slice(segment.start, segment.end + 1)
                                                    .filter((rate) => after || rate.status !== LessonBookingPlannerRateStatus.New)
                                                    .reduce((total, rate) => total + (after ? rate.amountAfter : rate.amountBefore), 0),
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                data.rates.slice(segment.start, segment.end + 1).map((rate, offset) => rateEntry(rate, segment.start + offset, after))
                            ),
                        )}
                        {totalPrice(after)}
                        {/* Die Zahlweise gehört in die Buchung, nicht in die Kursänderung. Links eine unsichtbare Zeile gleicher Höhe,
                            damit „Notizen“ auf beiden Seiten auf einer Höhe beginnt (Stage-Abnahme 27.10.). */}
                        {booking && !data.canceled && booking.bookingType !== BookingType.Waitlist && (
                            <div className={css.paymentTypeHint} style={after ? undefined : {visibility: "hidden"}} aria-hidden={!after}>
                                {"Die Zahlweise lässt sich in der Buchung selbst ändern."}
                            </div>
                        )}
                    </div>
                }
            />
            {booking && (
                <SummarySection
                    style={{marginTop: 12}}
                    title={i18n.containers.bookings.BookingSummary.sectionHeaders.notes()}
                    content={<Notes booking={booking} staffNote={after ? intent?.staffNote : undefined} onStaffNoteChange={after && editable ? (value) => onIntentChange?.({staffNote: value}) : undefined} />}
                />
            )}
        </div>
    );

    // Kurseinheiten unter beiden Spalten, zugeklappt eine Zeile (Variante A, 27.10.2026): Monate und Raten stehen so direkt
    // untereinander. Die Zeile nennt neue Termine im Kurs, die diese Buchung noch nicht hat.
    const lessonTable = (after: boolean) => (
        <Table<LessonRow>
            className="lessonSelectionTable"
            size="small"
            rowKey="key"
            dataSource={lessons}
            columns={lessonColumns(after)}
            rowSelection={{
                type: "checkbox",
                hideSelectAll: true,
                selectedRowKeys: after ? selectedKeys : lessons.filter((row) => row.before).map((row) => row.key),
                getCheckboxProps: (row) => ({disabled: !(after && editable && row.inCourse), style: after || row.existed ? undefined : {visibility: "hidden"}}),
                onSelect: (row, selected) => selectLessons(selected ? [...selectedKeys, row.key] : selectedKeys.filter((key) => key !== row.key)),
                // Shift-Klick über mehrere Zeilen
                onSelectMultiple: (selected, _rows, changed) => {
                    const keys = changed.map((row) => row.key);

                    selectLessons(selected ? Array.from(new Set([...selectedKeys, ...keys])) : selectedKeys.filter((key) => keys.indexOf(key) === -1));
                },
            }}
            rowClassName={(row) => lessonRowClass(row, after)}
            pagination={pagination}
            onChange={onTableChange}
        />
    );
    const inCourseCount = lessons.filter((row) => row.inCourse).length;
    const newNotSelected = lessons.filter((row) => row.inCourse && !row.existed && selectedKeys.indexOf(row.key) === -1);
    const lessonsSection = lessonsOfCourse && (
        <div className={classNames(css.lessonsSection, lessonsExpanded && css.lessonsSectionOpen)} data-testid="planner-lessons">
            <div className={css.lessonsBar}>
                <span>
                    <b>{i18n.containers.bookings.BookingSummary.sectionHeaders.courseLessons()}</b>
                    {/* Nach „Lektionen neu generieren“ gibt es keine Zuordnung, eine Zahl wäre falsch (Review #1728). */}
                    {data.regenerated ? " · Zuordnung zu den neuen Terminen offen, der Bestand bleibt" : ` · ${selectedKeys.length} von ${inCourseCount} ${editable ? "angehakt" : "gebucht"}`}
                    {editable && newNotSelected.length > 0 && (
                        <span className={css.lessonsHint}>
                            {` · ${newNotSelected.length === 1 ? `Neuer Termin ${date(newNotSelected[0].startDateTime)}` : `${newNotSelected.length} neue Termine`} noch nicht angehakt`}
                        </span>
                    )}
                </span>
                {lessonsExpanded && editable && (
                    // „Mehrere Einheiten bearbeiten“: der Zeitraum ersetzt die Auswahl, wie im bisherigen Dialog.
                    <div style={{flexBasis: showPicker ? "100%" : 200, order: showPicker ? 3 : 0}}>
                        <DateRangePicker
                            showPicker={showPicker}
                            setShowPicker={setShowPicker}
                            selectedLessonIds={selectedKeys}
                            setSelectedLessonIds={(keys) => selectLessons(keys.map(String))}
                            lessonsOfCourse={courseLessonsForPicker}
                            getHolidayInfo={getHolidayInfo ?? (() => undefined)}
                            holidays={holidays}
                        />
                    </div>
                )}
                <Button size="small" type={lessonsExpanded ? "link" : "default"} style={{marginLeft: "auto"}} onClick={() => setLessonsExpanded(!lessonsExpanded)}>
                    {lessonsExpanded ? "Zuklappen ▴" : "Aufklappen ▾"}
                </Button>
            </div>
            {lessonsExpanded && panels(lessonTable(false), lessonTable(true), false)}
        </div>
    );

    const list = (items: Array<string>) => (
        <ul style={{margin: 0, paddingLeft: "1.25rem"}}>
            {items.map((item, index) => (
                <li key={index}>{item}</li>
            ))}
        </ul>
    );

    const decision = data.canceled ? (
        <Alert
            type="info"
            showIcon
            message="Buchung ist storniert"
            description={
                <>
                    {"Einheiten werden abgeglichen, Preis und Raten bleiben unverändert."}
                    {data.warnings.length > 0 && list(data.warnings)}
                </>
            }
        />
    ) : data.warnings.length === 0 ? (
        <Alert
            type="success"
            showIcon
            message="Nichts zu entscheiden"
            description={handEdited ? "Raten von Hand geändert, der Preis folgt den Raten." : "Buchung konsistent: Preis = Einheiten × Einheitenpreis, Raten decken den Preis."}
        />
    ) : (
        <Alert type="warning" showIcon message="Was das Büro entscheiden muss" description={list(data.warnings)} />
    );

    // Entscheidungen des Büros für diese Buchung; der Server rechnet sie in die Vorschau ein. Preismodus steht beim
    // Gesamtpreis, die Notiz Mitarbeiterin bei den Notizen der rechten Spalte.
    const showOffset = data.ambiguousOffset || (intent?.rateMonthOffset !== null && intent?.rateMonthOffset !== undefined);
    const officeDecisions = editable && (
        <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, marginTop: 8}}>
            {showOffset && (
                <Space>
                    <span>{"Rate für die Einheiten eines Monats fällig"}</span>
                    <Select<number>
                        size="small"
                        style={{width: 180}}
                        aria-label="Monatsversatz"
                        placeholder="bitte bestätigen"
                        value={intent?.rateMonthOffset ?? undefined}
                        options={OFFSET_OPTIONS}
                        onChange={(value) => onIntentChange?.({rateMonthOffset: value})}
                    />
                </Space>
            )}
        </div>
    );

    // Was das Büro an dieser Buchung gegenüber dem Vorschlag geändert hat, je Punkt rücknehmbar, dazu ein Weg zurück zum
    // ursprünglichen Vorschlag (Stage-Abnahme 27.10.: nach Verklicken war unklar, was sich geändert hat und wie man zurückkommt).
    const isSet = (value: unknown) => value !== null && value !== undefined;
    const ownChanges: Array<{key: string; text: string; undo: Partial<LessonBookingIntentInput>}> = [];

    if (intent?.courseLessonIds) ownChanges.push({key: "lessons", text: "Kurseinheiten von Hand angehakt", undo: {courseLessonIds: null}});
    if (isSet(intent?.individualPrice) || isSet(intent?.chargeUnitChanges)) {
        ownChanges.push({
            key: "mode",
            text: intent?.individualPrice === false ? `auf Einheiten × ${eur(unitPrice)} umgestellt` : unitsCharged ? "geänderte Einheiten verrechnen" : "Preis bleibt trotz geänderter Einheiten",
            undo: {individualPrice: null, chargeUnitChanges: null},
        });
    }
    if (intent?.rateEdits?.length) {
        const n = intent.rateEdits.length;

        ownChanges.push({key: "rates", text: n === 1 ? "1 Rate von Hand geändert" : `${n} Raten von Hand geändert`, undo: {rateEdits: null}});
    }
    if (isSet(intent?.rateMonthOffset)) ownChanges.push({key: "offset", text: "Monat der Rate bestätigt", undo: {rateMonthOffset: null}});
    if (isSet(intent?.staffNote)) ownChanges.push({key: "staffNote", text: "Notiz Mitarbeiterin geändert", undo: {staffNote: null}});
    if (intent?.note) ownChanges.push({key: "note", text: "Notiz zur Änderung", undo: {note: null}});

    const changesBar = editable && ownChanges.length > 0 && (
        <Alert
            type="warning"
            style={{marginBottom: 8}}
            message={
                <div style={{display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"}}>
                    <strong>{"Deine Änderungen am Vorschlag:"}</strong>
                    {ownChanges.map((change) => (
                        <span key={change.key} style={{whiteSpace: "nowrap"}}>
                            {change.text}
                            <Tooltip title="Diese Änderung zurücknehmen">
                                <Button type="link" size="small" icon={<UndoOutlined />} onClick={() => onIntentChange?.(change.undo)} aria-label={`${change.text} zurücknehmen`} />
                            </Tooltip>
                        </span>
                    ))}
                    <Button size="small" icon={<UndoOutlined />} style={{marginLeft: "auto"}} onClick={() => onIntentChange?.(null)}>
                        {"Zurück zum ursprünglichen Vorschlag"}
                    </Button>
                </div>
            }
        />
    );

    // Variante A (27.10.2026): Kurzfassung zuerst, dann Teilnehmer/Bucher und Hinweis, dann Monate und Raten nebeneinander,
    // darunter die Kurseinheiten und was das Büro entscheidet.
    return (
        <>
            <PlannerSummary data={data} intent={intent} isWaitlist={booking?.bookingType === BookingType.Waitlist} />
            {header}
            {changesBar}
            {panels(side(false), side(true))}
            {lessonsSection}
            <div style={{marginTop: 8}}>{decision}</div>
            {officeDecisions}
        </>
    );
};

export default memo(PlannerPreview);
